"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { InstagramIcon, TikTokIcon } from "@/features/user/ui/components/icons";
import { Link } from "@/i18n/navigation";
import { CountrySelector } from "@/lib/client/components/CountrySelector";
import { CookiePreferences } from "@/lib/client/consent";
import { footerNavigation } from "@/lib/shared/constants";
import {
  Box,
  Button,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Grid,
  HStack,
  Input,
  Section,
  Separator,
  Text,
  VStack,
} from "@/ui";

// define form schema
export const formSchema = z.object({
  email: z.string().trim().min(1).max(50).email(),
});

/**
 * Footer component.
 * @returns Footer with navigation, newsletter, and social links.
 */
export const Footer = () => {
  const footerT = useTranslations("footer");
  const validationT = useTranslations("validation");
  const generalT = useTranslations("general");
  const navigationT = useTranslations("navigation");
  const toastT = useTranslations("toast");
  const locale = useLocale();
  const [isLoading, setIsLoading] = React.useState(false);
  const [cookiePrefsOpen, setCookiePrefsOpen] = React.useState(false);

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const res = await fetch(`/${locale}/api/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, locale }),
      });

      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`);
      }

      form.reset();
      toast.success(toastT("success.newsletter_message"));
    } catch {
      toast.error(footerT("newsletter_error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section
      id="footer"
      className="mt-auto border-t border-border/50 bg-muted/30 py-8 md:py-12"
    >
      <Box className="container-luxury">
        <Grid className="grid-cols-1 py-8 md:grid-flow-col md:auto-rows-min md:grid-cols-12 md:gap-x-8 md:gap-y-12">
          {/* Sitemap sections */}
          <Grid className="col-span-6 mt-10 grid-cols-2 gap-8 sm:grid-cols-3 md:col-span-8 md:col-start-3 md:row-start-1 md:mt-0 lg:col-span-6 lg:col-start-2">
            <Grid className="grid-cols-1 gap-y-12 py-3 sm:col-span-2 sm:grid-cols-2 sm:gap-x-8">
              <VStack className="space-y-3">
                <Link href="/">
                  <Text className="font-serif text-2xl font-medium tracking-tight">TUTULY</Text>
                </Link>
                {/* Trust microcopy */}
                <Text className="text-xs text-muted-foreground max-w-[200px] leading-relaxed">
                  Sterling silver jewelry with 18k gold plating. Designed for the modern woman.
                </Text>
                {footerNavigation.company.map((item) => (
                  <Link
                    href={item.href}
                    key={item.name}
                    className={"w-fit"}
                  >
                    <Text className="text-sm text-foreground/75">{footerT(item.name)}</Text>
                  </Link>
                ))}
                <HStack className={"items-center space-x-2"}>
                  <Text className={"text-sm text-foreground/75"}>{footerT("follow")}</Text>
                  <Link
                    href={"https://www.instagram.com/tutulyjewellery"}
                    target={"_blank"}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <InstagramIcon className={"h-6 w-6"} />
                  </Link>
                  <Link
                    href={"https://www.tiktok.com/@tutulyjewellery"}
                    target={"_blank"}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <TikTokIcon className={"h-6 w-6"} />
                  </Link>
                </HStack>
              </VStack>
              <Grid className={"grid-cols-1 space-y-2"}>
                <Text className="col-span-1 font-medium">{footerT("products")}</Text>
                {footerNavigation.products.map((item) => (
                  <Link
                    href={item.href}
                    key={item.name}
                    className={"w-fit"}
                  >
                    <Text className="text-sm text-foreground/75">{navigationT(item.name)}</Text>
                  </Link>
                ))}
              </Grid>
            </Grid>
            <VStack className={"space-y-2 py-3"}>
              <Text className="font-medium">{footerT("customer_services")}</Text>
              {footerNavigation.customerService.map((item) => (
                <Link
                  href={item.href}
                  key={item.name}
                  className={"w-fit"}
                >
                  <Text className="text-sm text-foreground/75">{footerT(item.name)}</Text>
                </Link>
              ))}
            </VStack>
          </Grid>

          {/* Newsletter section */}
          <div className="mt-12 md:col-span-8 md:col-start-3 md:row-start-2 md:mt-0 lg:col-span-4 lg:col-start-9 lg:row-start-1">
            <Text className="font-medium">{footerT("newsletter")}</Text>
            <Text className="my-2 text-sm text-foreground/75">
              {footerT("newsletter_description")}
            </Text>

            <Form {...form}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className={"join flex"}
              >
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className={"grow"}>
                      <FormControl>
                        <Input
                          className={"join-item"}
                          placeholder={footerT("newsletter_placeholder")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage>{errors.email && validationT("email")}</FormMessage>
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  variant={"default"}
                  disabled={isLoading}
                  className={"join-item"}
                >
                  <span className={cn(isLoading ? "loading loading-spinner" : "hidden")} />
                  {generalT("submit")}
                </Button>
              </form>
            </Form>
            <HStack className={"mt-2 items-center space-x-2"}>
              <Text className="font-medium">{footerT("ship_to")}</Text>
              <CountrySelector />
            </HStack>
          </div>
        </Grid>

        <Separator className="my-2" />
        <HStack className={"flex-wrap items-center justify-center space-x-2 pt-4"}>
          <Text className="text-xs leading-5 text-foreground/75">{footerT("copyright")}</Text>
          <Link href={"/privacy-policy"}>
            <Text className={"text-xs text-foreground/75"}>{footerT("privacy_policy")}</Text>
          </Link>
          <Link href={"/terms"}>
            <Text className={"text-xs text-foreground/75"}>{footerT("terms")}</Text>
          </Link>
          <button
            type="button"
            onClick={() => setCookiePrefsOpen(true)}
            className="text-xs text-foreground/75 hover:underline"
          >
            {footerT("cookie_settings")}
          </button>
        </HStack>
      </Box>

      <CookiePreferences
        open={cookiePrefsOpen}
        onOpenChange={setCookiePrefsOpen}
      />
    </Section>
  );
};
